/**
 * Repair the two ways an answer's markdown arrives malformed, before it is parsed.
 *
 * Both are the model or the knowledge base producing markdown that CommonMark then
 * reads differently than anyone intended, and both have to be fixed on the raw string
 * because by the time there is a tree the evidence has been rearranged.
 *
 * ## 1. Whitespace inside a link destination
 *
 * Knowledge-base answers carry URLs that were pasted out of documents, and the paste
 * brings the document's line wrapping with it: a URL that wrapped after a hyphen
 * arrives as `https://host/weten-en- regelen/...`, with a literal space in it.
 *
 * A CommonMark link destination may not contain unescaped whitespace, so
 * `[Handleidingen Xedule](https://host/weten-en- regelen/x)` is not a link at all.
 * The parser emits the literal `[`, `]` and `(` characters, and remark-gfm's autolink
 * extension then links the bare URL prefix up to the first space. The user reads
 * visible markdown syntax with one clickable fragment in the middle of it -- reported
 * as "part is blue and the rest is black" (MA-222).
 *
 * ## Why this runs on the string and not on the tree
 *
 * `remarkCitations` deliberately works on the parsed tree, and its reasoning applies
 * here in reverse. It can work on the tree because `[docN]` survives parsing as text.
 * A broken link does not survive: by the time there is a tree, the construct has been
 * scattered across a text node, an autolink node and another text node, and there is
 * no link node to repair. The raw string is the only place the construct still exists
 * whole, so the repair has to happen before the parse.
 *
 * That means taking on the problem `remarkCitations` avoided: a string-level pass
 * cannot see that it is inside a code span, and rewriting a URL somebody meant to
 * read literally would be the same class of bug. Hence the scanner below tracks
 * fenced blocks and inline code and passes them through untouched.
 *
 * ## What it will not touch
 *
 * The repair only fires on a destination that is unambiguously a broken URL, because
 * stripping whitespace from anything else corrupts working markdown:
 *
 * - a destination with a quote in it may carry a link title (`[a](https://x "T")`),
 *   where the space is structural;
 * - an angle-bracketed destination (`[a](<https://x y>)`) is *allowed* to hold
 *   spaces and already works;
 * - a destination containing `(` may use balanced parentheses, which this scanner
 *   does not track;
 * - anything that is not `http://` or `https://`, or is implausibly long.
 *
 * In all of those cases the text is left exactly as it came in. The function is pure
 * and returns the input unchanged when there is nothing to repair.
 *
 * ## 2. A citation marker glued to a bare URL
 *
 * `https://example.com/x[doc1]`, with no space, is read by remark-gfm's autolink as one
 * URL ending `x%5Bdoc1`: `[` is not a character it trims from the end. The link is dead
 * and the citation is lost, because `remarkCitations` only ever sees text nodes and the
 * marker is no longer one. `fenceGluedCitationMarkers` puts the URL in `<...>`, whose
 * delimiters are explicit, so the marker stays outside it.
 *
 * That defect predates this module -- it is not caused by the whitespace repair, and it
 * reproduces identically with the repair removed.
 *
 * ## Limitations
 *
 * One is deliberate: a destination split by a *newline* rather than
 * by a space is not repaired. The scanner works a line at a time so that it can track
 * fenced blocks, so such a destination has no closing paren on its line. The defect
 * observed in the knowledge base is spaces, because the ingest collapses the source
 * document's line breaks into them.
 */

/** A fenced code block opens or closes on a line starting with ``` or ~~~. */
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;

/** Only http(s) destinations are repaired; see the note on what is left alone. */
const URL_START_RE = /^https?:\/\//i;

/** Past this, a `](` was almost certainly not a link destination at all. */
const MAX_DESTINATION_LENGTH = 500;

/** A bare URL with a citation marker glued straight onto it, anchored at the cursor. */
const GLUED_MARKER_RE = /^(https?:\/\/[^\s<>()[\]]+)(\[doc\d+\])/i;

/**
 * Fence a bare URL off from a citation marker stuck to its end.
 *
 * When the model writes `https://example.com/x[doc1]` with no space, remark-gfm's
 * autolink-literal extension takes the marker to be part of the URL: `[` is not in the
 * set of trailing characters it trims, so the href ends `...x%5Bdoc1` and the marker
 * never survives as a text node for `remarkCitations` to turn into a source button. The
 * user gets a dead link and a lost citation from one missing space.
 *
 * Wrapping the URL in angle brackets is CommonMark's explicit autolink form. It renders
 * exactly the same — link text is the URL either way — but its delimiters are stated
 * rather than guessed, so the marker after it stays outside and stays a text node.
 *
 * The alternative, inserting a space, would work too and is rejected on purpose: it puts
 * a character in the answer that the model did not write.
 *
 * Matched at the cursor rather than over the whole line, so that `repairLine`'s walk --
 * which already steps over code spans -- decides where this may fire. A whole-line
 * regex rewrote URLs inside backticks, which is the same mistake `remarkCitations` was
 * written to avoid.
 */
function fenceGluedMarker(
  line: string,
  index: number,
): { text: string; consumed: number } | null {
  // A URL preceded by `(` is a link destination, where this repair would corrupt the
  // link rather than fix it.
  if (index > 0 && line[index - 1] === "(") return null;

  const match = GLUED_MARKER_RE.exec(line.slice(index));
  if (!match) return null;

  return { text: `<${match[1]}>${match[2]}`, consumed: match[0].length };
}

/**
 * The repaired `](...)` span starting at `index`, or null to leave it alone.
 *
 * `index` points at the `]`. Returns the replacement text and how many characters of
 * the input it consumes, so the caller can advance without rescanning.
 */
function repairDestination(
  line: string,
  index: number,
): { text: string; consumed: number } | null {
  const open = index + 2; // past "]("
  const close = line.indexOf(")", open);
  if (close === -1) return null;

  const destination = line.slice(open, close);
  if (destination.length > MAX_DESTINATION_LENGTH) return null;

  // A `(` inside means balanced parentheses this scanner cannot reason about.
  if (destination.includes("(")) return null;

  const trimmed = destination.trim();
  // No whitespace between non-whitespace parts: either already fine, or it is only
  // the padding CommonMark allows around a destination.
  if (!/\s/.test(trimmed)) return null;
  // A quote may be a link title, where the whitespace is structural.
  if (/["']/.test(trimmed)) return null;
  // `<...>` destinations are permitted to contain spaces and already work.
  if (trimmed.startsWith("<")) return null;
  if (!URL_START_RE.test(trimmed)) return null;

  return {
    text: `](${trimmed.replace(/\s+/g, "")})`,
    consumed: close + 1 - index,
  };
}

/**
 * Repair one line that is known not to be inside a fenced block.
 *
 * Inline code is skipped span by span: a run of N backticks is closed by the next run
 * of N backticks on the same line. A span that never closes takes the rest of the
 * line with it, which errs toward leaving text alone.
 */
function repairLine(line: string): string {
  let out = "";
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === "`") {
      let run = 0;
      while (line[i + run] === "`") run += 1;
      const fence = "`".repeat(run);
      const closing = line.indexOf(fence, i + run);
      // Unclosed: the remainder is code as far as we can tell, so copy it as is.
      const end = closing === -1 ? line.length : closing + run;
      out += line.slice(i, end);
      i = end;
      continue;
    }

    if (char === "h" || char === "H") {
      const fenced = fenceGluedMarker(line, i);
      if (fenced) {
        out += fenced.text;
        i += fenced.consumed;
        continue;
      }
    }

    if (char === "]" && line[i + 1] === "(") {
      const repaired = repairDestination(line, i);
      if (repaired) {
        out += repaired.text;
        i += repaired.consumed;
        continue;
      }
    }

    out += char;
    i += 1;
  }

  return out;
}

/** Repair every broken http(s) link destination outside code. Pure. */
export default function normalizeLinkDestinations(markdown: string): string {
  // The overwhelmingly common case is a plain answer with neither construct in it. Both
  // repairs have to be named here: this guard once tested `](` alone, from when that was
  // the only repair, and it silently skipped every glued citation marker -- which is
  // exactly the shape of the defect the second repair exists for.
  if (!markdown.includes("](") && !markdown.includes("[doc")) return markdown;

  const lines = markdown.split("\n");
  let inFence = false;
  let fenceChar = "";
  let fenceLength = 0;

  const repaired = lines.map((line) => {
    const fence = FENCE_RE.exec(line);
    if (fence) {
      const marker = fence[1];
      if (!inFence) {
        inFence = true;
        fenceChar = marker[0];
        fenceLength = marker.length;
      } else if (marker[0] === fenceChar && marker.length >= fenceLength) {
        inFence = false;
        fenceChar = "";
        fenceLength = 0;
      }
      return line;
    }

    return inFence ? line : repairLine(line);
  });

  return repaired.join("\n");
}
